import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Search, Save, ShieldCheck, Check, ChevronDown, ChevronRight } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { normalizeForMatch } from '@/lib/validators'
import { cn } from '@/lib/cn'
import { toast } from '@/components/ui/Toast'
import { HierarchiesService, type PermissionGroup } from '@/services/modules/HierarchiesService'
import { useAuthority } from '@/hooks/useAuthority'

export function HierarchyFormPage() {
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const editing = !!id
  const { can } = useAuthority()
  const canSave = can(editing ? 'administractive.hierarchies.edit' : 'administractive.hierarchies.create')

  const [groups, setGroups] = useState<PermissionGroup[]>([])
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [nameError, setNameError] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all([
      HierarchiesService.permissionsCatalog(),
      editing ? HierarchiesService.show(id!) : Promise.resolve(null),
    ]).then(([cat, h]) => {
      if (!active) return
      setGroups(cat)
      if (h) {
        setName(h.name)
        setStatus(h.status)
        setSelected(new Set(h.authorities ?? []))
      }
    }).catch(() => toast.error('Erro ao carregar o catálogo de permissões.'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [id, editing])

  const allKeys = useMemo(
    () => groups.flatMap(g => g.resources.flatMap(r => r.permissions.map(p => p.key))),
    [groups],
  )
  const totalSelected = selected.size

  // filtro de texto sobre label/chave/descrição (em qualquer nível)
  const filteredGroups = useMemo(() => {
    const q = normalizeForMatch(search)
    if (!q) return groups
    return groups
      .map(g => {
        const groupHit = normalizeForMatch(g.label).includes(q)
        const resources = g.resources
          .map(r => {
            const resHit = groupHit || normalizeForMatch(r.label).includes(q)
            const permissions = resHit ? r.permissions : r.permissions.filter(p =>
              normalizeForMatch(p.label).includes(q) ||
              normalizeForMatch(p.key).includes(q) ||
              normalizeForMatch(p.description ?? '').includes(q),
            )
            return { ...r, permissions }
          })
          .filter(r => r.permissions.length > 0)
        return { ...g, resources }
      })
      .filter(g => g.resources.length > 0)
  }, [groups, search])

  function setMany(keys: string[], on: boolean) {
    setSelected(prev => {
      const next = new Set(prev)
      keys.forEach(k => (on ? next.add(k) : next.delete(k)))
      return next
    })
  }
  function toggle(key: string) { setMany([key], !selected.has(key)) }
  function toggleCollapse(gkey: string) {
    setCollapsed(prev => { const n = new Set(prev); n.has(gkey) ? n.delete(gkey) : n.add(gkey); return n })
  }

  async function submit() {
    if (!name.trim()) {
      setNameError('Informe o nome da hierarquia.')
      toast.error({ title: 'Nome obrigatório', description: 'Informe o nome da hierarquia.' })
      return
    }
    setSaving(true)
    try {
      const payload = { name: name.trim(), authorities: Array.from(selected), status }
      if (editing) { await HierarchiesService.update(id!, payload); toast.success({ title: 'Hierarquia atualizada', description: name }) }
      else { await HierarchiesService.create(payload); toast.success({ title: 'Hierarquia criada', description: name }) }
      nav('/administractive/hierarchies')
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    } finally { setSaving(false) }
  }

  return (
    <>
      <PageHeader
        title={editing ? 'Editar hierarquia' : 'Nova hierarquia'}
        subtitle="Defina o nome do grupo e selecione as permissões liberadas"
        backTo="/administractive/hierarchies"
        crumbs={[{ label: 'Administração' }, { label: 'Hierarquias' }, { label: editing ? 'Editar' : 'Nova' }]}
        actions={<Button onClick={submit} loading={saving} disabled={!canSave}
          title={canSave ? undefined : `Sem permissão para ${editing ? 'editar' : 'criar'} hierarquias`}
          iconLeft={<Save size={14} />}>Salvar</Button>}
      />

      {/* Dados */}
      <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-2">
            <Input label="Nome da hierarquia" required value={name}
              onChange={e => { setName(e.target.value); if (nameError) setNameError('') }}
              placeholder="Ex.: Gestor comercial, Analista de credenciamento" error={nameError} />
          </div>
          <StatusToggle value={status} onChange={setStatus} />
        </div>
      </div>

      {/* Permissões */}
      <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-500" />
            <h2 className="text-sm font-semibold text-gray-800">Permissões</h2>
            <Badge variant={totalSelected ? 'blue' : 'gray'}>{totalSelected} selecionada(s)</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button size="xs" variant="ghost" onClick={() => setMany(allKeys, true)}>Marcar tudo</Button>
            <Button size="xs" variant="ghost" onClick={() => setSelected(new Set())}>Limpar tudo</Button>
          </div>
        </div>

        <div className="mb-4 max-w-md">
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filtrar permissões (ex.: assinatura, excluir, comissão)"
            iconLeft={<Search size={14} />} clearable />
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">Carregando permissões</div>
        ) : filteredGroups.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">Nenhuma permissão encontrada para “{search}”.</div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map(g => {
              const keys = g.resources.flatMap(r => r.permissions.map(p => p.key))
              const selCount = keys.filter(k => selected.has(k)).length
              const allOn = selCount === keys.length && keys.length > 0
              const isCollapsed = collapsed.has(g.key) && !search
              return (
                <div key={g.key} className="rounded-md ring-1 ring-gray-200 overflow-hidden">
                  {/* Nível 1: Módulo */}
                  <div className="flex items-center justify-between gap-3 bg-gray-100 px-4 py-2.5">
                    <button type="button" onClick={() => toggleCollapse(g.key)}
                      className="cursor-pointer flex items-center gap-2 min-w-0 text-left">
                      {isCollapsed ? <ChevronRight size={15} className="text-gray-400 shrink-0" /> : <ChevronDown size={15} className="text-gray-400 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 break-words">{g.label}</div>
                        {g.description && <div className="text-[11px] text-gray-500 break-words">{g.description}</div>}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={selCount ? 'blue' : 'gray'}>{selCount}/{keys.length}</Badge>
                      <Button size="xs" variant="ghost" onClick={() => setMany(keys, !allOn)}>
                        {allOn ? 'Desmarcar' : 'Marcar módulo'}
                      </Button>
                    </div>
                  </div>

                  {/* Nível 2: Recursos */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {g.resources.map(r => {
                        const rKeys = r.permissions.map(p => p.key)
                        const rSel = rKeys.filter(k => selected.has(k)).length
                        const rAll = rSel === rKeys.length && rKeys.length > 0
                        return (
                          <div key={r.key}>
                            <div className="flex items-center justify-between gap-3 bg-white px-4 py-2 border-l-2 border-brand-400">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[13px] font-semibold text-gray-800 break-words">{r.label}</span>
                                <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold',
                                  rSel ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-400')}>
                                  {rSel}/{rKeys.length}
                                </span>
                              </div>
                              <button type="button" onClick={() => setMany(rKeys, !rAll)}
                                className={cn('cursor-pointer shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition',
                                  rAll ? 'text-gray-500 hover:bg-gray-100' : 'text-brand-700 bg-brand-50 hover:bg-brand-100')}>
                                <Check size={11} strokeWidth={3} /> {rAll ? 'Desmarcar todos' : 'Marcar todos'}
                              </button>
                            </div>
                            {/* Nível 3: Ações */}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-gray-100">
                              {r.permissions.map(p => {
                                const on = selected.has(p.key)
                                return (
                                  <button key={p.key} type="button" onClick={() => toggle(p.key)}
                                    className={`cursor-pointer text-left px-3 py-2.5 flex items-start gap-2.5 transition hover:bg-brand-50/40 ${on ? 'bg-brand-50/60' : 'bg-white'}`}>
                                    <span className={`mt-0.5 grid place-items-center h-4 w-4 rounded shrink-0 ring-1 transition ${
                                      on ? 'bg-brand-500 ring-brand-500 text-white' : 'bg-white ring-gray-300 text-transparent'}`}>
                                      <Check size={11} strokeWidth={3} />
                                    </span>
                                    <span className="min-w-0">
                                      <span className="block text-[13px] font-medium text-gray-800 leading-tight">{p.label}</span>
                                      {p.description && <span className="block text-[11px] text-gray-500 leading-tight mt-0.5">{p.description}</span>}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
